import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Chart, BarController, BarElement, CategoryScale, LinearScale } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { useViewStore, useGraphStore } from '../store';

Chart.register(ChartDataLabels, BarController, BarElement, CategoryScale, LinearScale);

function MultipleBarCharts({ graphData, graphDescription, showValues, showKey }) {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const graphTableView = useGraphStore((state) => state.graphTableView);
    const [isMobile, setIsMobile] = useState(false);
    const { labels, years, previousData, currentData, transformPreviousCurrentData, backgroundColors } = useMemo(() => {
        const years = ["2023", "2024"];
        const currentPeriodItems = graphData.currentPeriod.items;
        const previousPeriodItems = graphData.previousPeriod.items;

        const transformPreviousCurrentData = previousPeriodItems.map((item, index) => ({
            year: years[index],
            label: item.title,
            color: item.color,
            data: [
                new Intl.NumberFormat("en-GB").format(previousPeriodItems[index]?.value) || '—',
                new Intl.NumberFormat("en-GB").format(currentPeriodItems[index]?.value) || '—'
            ],
        })); 

        return {
            labels: previousPeriodItems.map(item => item.title),
            previousData: previousPeriodItems.map(item => item.value),
            currentData: currentPeriodItems.map(item => item.value),
            backgroundColors: previousPeriodItems.map(item => item.color),
            transformPreviousCurrentData
        };
    }, [graphData]);

    useEffect(() => {

        const handleResize = () => {
            setIsMobile(window.innerWidth <= 992); // 992px is a common breakpoint for mobile devices
        };

        window.addEventListener('resize', handleResize);

        // Set initial state on mount
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {

        const currentView = useViewStore.getState().currentView;

        const chartAreaBorder = {
            id: 'chartAreaBorder',
            afterDraw(chart, args, options) {
                const { ctx, chartArea: { left, top, width, height } } = chart;
                ctx.save();
                ctx.strokeStyle = options.borderColor || 'black';
                ctx.lineWidth = 2;

                // Bottom border
                ctx.beginPath();
                ctx.moveTo(left, top + height);
                ctx.lineTo(left + width, top + height);
                ctx.stroke();

                // Left border
                ctx.strokeStyle = 'white';

                ctx.beginPath();
                ctx.moveTo(left, top - 8);
                ctx.lineTo(left, top + height);
                ctx.stroke();
            
                // Right border
                ctx.beginPath();
                ctx.moveTo(left + width, top - 8);
                ctx.lineTo(left + width, top + height);
                ctx.stroke();

                ctx.restore();
            }
        };
        

        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        if (chartRef.current) {
            chartInstance.current = new Chart(chartRef.current.getContext('2d'), {
                type: 'bar',
                data: {
                    labels,
                    datasets: [
                        {
                            label: '2023',
                            backgroundColor: backgroundColors.map(color => `${color}70`),
                            data: previousData,
                            barPercentage: 1,
                            categoryPercentage: 0.7,
                        },
                        {
                            label: '2024',
                            backgroundColor: backgroundColors,
                            data: currentData,
                            barPercentage: 1,
                            categoryPercentage: 0.7,
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                        padding: {
                            left: currentView === 'DetailsView' ? 30 : 0,
                            right: currentView === 'DetailsView' ? 30 : 0,
                            top: currentView === 'DetailsView' ? 10 : 0
                        }
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: { enabled: false },
                        datalabels: {
                            anchor: 'end',
                            align: 'top',
                            color: 'black',
                            font: {
                                size: 10,
                                weight: 'bold'
                            },
                            formatter: function(value, context) {
                                return currentView === 'DetailsView' ? context.chart.data.datasets[context.datasetIndex].label : '';
                            }
                        }
                    },
                    scales: {
                        x: {
                            display: !isMobile && currentView === 'DetailsView' && showValues,
                            grid: { 
                                color: 'black',
                                drawBorder: false,
                            },     
                            position: 'top',
                            ticks: {
                                display: !isMobile && currentView === 'DetailsView',
                                font: { 
                                    size: 10, 
                                    weight: 'bold' 
                                },
                                color: 'black',
                                padding: 20
                            },
                            ticks: {
                                display: false 
                            }
                        },
                        y: {
                            display: false,
                        }
                    }
                },
                 plugins: [
                    !isMobile && currentView === 'DetailsView'? chartAreaBorder : '', 
                    // leftBarBorder
                ]
            });
        }

        return () => chartInstance.current?.destroy();
    }, [labels, previousData, currentData, backgroundColors, showValues]);

    return (
        <div className={`chart-wrapper ${graphTableView} multiple-bar-charts`}>   
            <h3 className="chart-title">{graphDescription}</h3>
            <div className="chart bar-chart-wrapper">
                <canvas className="bar-chart" ref={chartRef}></canvas>
            </div>

            {showKey && (
                <>
                    <div className="legend display-none-up-lg">
                        {transformPreviousCurrentData.map((item, index) => (
                            <div key={index} className="legend-item">
                                <div
                                    className="legend-color"
                                    style={{
                                        backgroundColor: item.color,
                                    }}
                                ></div>
                                <span className="legend-text">{item.label}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {showKey && (
                    <>
                        <table className="table-legend">
                            <thead>
                                <tr>
                                    <th></th>
                                    {transformPreviousCurrentData.map((item, index) => {
                                        return (
                                        <th key={index}>
                                            {item.year}
                                        </th>
                                    )})}
                                </tr>
                            </thead>

                            <tbody>
                                {transformPreviousCurrentData.map((item, index) => {
                                    return(
                                        <tr className="legend-item" key={index}>
                                            <td>
                                                <span
                                                    className="legend-color"
                                                    style={{
                                                        backgroundColor: item.color,
                                                    }}
                                                ></span>
                                                {item.label}
                                            </td>
                                            <td>{item.data[0] != 0 ? item.data[0] : ' — '}</td>
                                            <td>{item.data[1] != 0 ? item.data[1] : ' — '}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </>
                )}
        </div>
    );
}

export default MultipleBarCharts;
